// ── Shared Styles (earthy orange palette) ────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia', serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Detail</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Helper: draw external gear teeth around a circle ─────────────────
function gearPath(cx, cy, r, teeth, toothH, phase = 0) {
  const th = toothH != null ? toothH : r * 0.18;
  const ro = r + th;
  const aW = (Math.PI * 0.55) / teeth;
  const pts = [];
  for (let i = 0; i < teeth; i++) {
    const a0 = phase + (2 * Math.PI * i) / teeth;
    pts.push(`${(cx + r  * Math.cos(a0 - aW*1.4)).toFixed(2)},${(cy + r  * Math.sin(a0 - aW*1.4)).toFixed(2)}`);
    pts.push(`${(cx + ro * Math.cos(a0 - aW*0.5)).toFixed(2)},${(cy + ro * Math.sin(a0 - aW*0.5)).toFixed(2)}`);
    pts.push(`${(cx + ro * Math.cos(a0 + aW*0.5)).toFixed(2)},${(cy + ro * Math.sin(a0 + aW*0.5)).toFixed(2)}`);
    pts.push(`${(cx + r  * Math.cos(a0 + aW*1.4)).toFixed(2)},${(cy + r  * Math.sin(a0 + aW*1.4)).toFixed(2)}`);
  }
  return 'M ' + pts.join(' L ') + ' Z';
}

// Helper: ellipse path
function ellipsePath(cx, cy, rx, ry, phase = 0) {
  const cos = Math.cos(phase), sin = Math.sin(phase);
  const pts = Array.from({length: 64}, (_, i) => {
    const t = (2 * Math.PI * i) / 64;
    const ex = rx * Math.cos(t), ey = ry * Math.sin(t);
    return `${(cx + ex*cos - ey*sin).toFixed(2)},${(cy + ex*sin + ey*cos).toFixed(2)}`;
  });
  return 'M ' + pts.join(' L ') + ' Z';
}

// Helper: ellipse with teeth (flexspline profile)
function flexsplinePath(cx, cy, rx, ry, teeth, toothH, phase = 0) {
  const cos = Math.cos(phase), sin = Math.sin(phase);
  const aW = (Math.PI * 0.50) / teeth;
  const pts = [];
  for (let i = 0; i < teeth; i++) {
    const a0 = phase + (2 * Math.PI * i) / teeth;
    const t0m = a0 - aW*1.4, t0p = a0 - aW*0.5;
    const t1m = a0 + aW*0.5, t1p = a0 + aW*1.4;
    // radius at this angle on ellipse
    const rBase = (t) => {
      const ct = Math.cos(t - phase), st = Math.sin(t - phase);
      return (rx * ry) / Math.sqrt((ry*ct)**2 + (rx*st)**2);
    };
    const push = (t, extra) => {
      const r = rBase(t) + extra;
      pts.push(`${(cx + r*Math.cos(t)).toFixed(2)},${(cy + r*Math.sin(t)).toFixed(2)}`);
    };
    push(t0m, 0); push(t0p, toothH); push(t1m, toothH); push(t1p, 0);
  }
  return 'M ' + pts.join(' L ') + ' Z';
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Full Anatomy: Wave Generator, Flexspline, Circular Spline
// ─────────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W = 960, H = 430;
  const cx = 300, cy = 212;
  // Circular spline — rigid ring, internal teeth
  const Rcs = 145; // inner face radius
  const nCS = 52;
  // Flexspline — elliptical, external teeth, 2 fewer teeth than CS
  const nFS = 50;
  const rxFS = 118, ryFS = 100; // semi-axes of ellipse
  // Wave generator — elliptical cam + bearing
  const rxWG = 112, ryWG = 94;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Harmonic Drive — Complete Anatomy of Wave Generator, Flexspline &amp; Circular Spline</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arHD" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <radialGradient id="csGrad" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor="#fdf4ec"/>
            <stop offset="100%" stopColor="#e8b07a" stopOpacity="0.4"/>
          </radialGradient>
        </defs>

        {/* ── Circular Spline (outermost — fixed ring, internal teeth) ── */}
        {/* Outer housing ring */}
        <circle cx={cx} cy={cy} r={Rcs + 28} fill="#d4874a" stroke="#7a2e00" strokeWidth="3" opacity="0.75"/>
        <circle cx={cx} cy={cy} r={Rcs + 28} fill="url(#csGrad)"/>
        {/* Internal teeth of circular spline */}
        {Array.from({length: nCS}, (_, i) => {
          const a0 = (2 * Math.PI * i) / nCS;
          const aW = (Math.PI * 0.50) / nCS;
          const ri = Rcs, rTip = Rcs - 14;
          const pts = [
            [ri * Math.cos(a0 - aW*1.4), ri * Math.sin(a0 - aW*1.4)],
            [rTip * Math.cos(a0 - aW*0.5), rTip * Math.sin(a0 - aW*0.5)],
            [rTip * Math.cos(a0 + aW*0.5), rTip * Math.sin(a0 + aW*0.5)],
            [ri * Math.cos(a0 + aW*1.4), ri * Math.sin(a0 + aW*1.4)],
          ].map(([x,y]) => `${(cx+x).toFixed(1)},${(cy+y).toFixed(1)}`).join(' ');
          return <path key={i} d={`M ${pts} Z`} fill="#c45a10" stroke="#7a2e00" strokeWidth="1" opacity="0.85"/>;
        })}

        {/* ── Flexspline (elliptical deformed cup, external teeth) ── */}
        {/* Flexspline body */}
        <path d={ellipsePath(cx, cy, rxFS, ryFS)} fill="#fdf4ec" stroke="none"/>
        <path d={flexsplinePath(cx, cy, rxFS + 2, ryFS + 2, nFS, 13)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.88"/>
        {/* Flexspline inner body */}
        <path d={ellipsePath(cx, cy, rxFS - 16, ryFS - 14)}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
        {/* Cup bottom / diaphragm */}
        <path d={ellipsePath(cx, cy, rxFS - 28, ryFS - 26)}
          fill="#fdf4ec" stroke="#a04010" strokeWidth="1.2" strokeDasharray="4,2" opacity="0.6"/>

        {/* ── Wave Generator (elliptical cam + thin bearing) ── */}
        {/* Bearing outer race */}
        <path d={ellipsePath(cx, cy, rxWG, ryWG)}
          fill="#e8b07a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.85"/>
        {/* Ball bearing balls (simplified) */}
        {Array.from({length: 18}, (_, i) => {
          const t = (2 * Math.PI * i) / 18;
          const rx = (rxWG + ryWG) / 2 - 4;
          const ry2 = (rxWG + ryWG) / 2 - 4;
          const bx = cx + rx * Math.cos(t);
          const by = cy + ry2 * Math.sin(t);
          return <circle key={i} cx={bx} cy={by} r={4} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1" opacity="0.8"/>;
        })}
        {/* Elliptical cam hub */}
        <path d={ellipsePath(cx, cy, rxWG - 18, ryWG - 16)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="2" opacity="0.9"/>
        {/* Input shaft hole */}
        <circle cx={cx} cy={cy} r={22} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={cx} cy={cy} r={8} fill="#7a2e00"/>
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">IN</text>

        {/* ── Mesh contact zones (two ellipse-poles) ── */}
        {[0, Math.PI].map((phase, i) => {
          const mx = cx + rxFS * Math.cos(phase);
          const my = cy + ryFS * Math.sin(phase);
          return (
            <g key={i}>
              <circle cx={mx} cy={my} r={18} fill="#7a2e00" opacity="0.18" stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="3,2"/>
              <text x={mx} y={my + 4} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">mesh</text>
            </g>
          );
        })}

        {/* ── Rotation arrows ── */}
        {/* Wave generator CW (input) — arc in top-right quadrant */}
        <path d={`M ${cx + rxWG - 8} ${cy - 18} A ${rxWG} ${ryWG} 0 0 1 ${cx + 18} ${cy - ryWG + 8}`}
          fill="none" stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arHD)"/>
        {/* ω_WG text above gear, right of centre */}
        <text x={cx + 60} y={cy - ryWG - 64} fontSize="9" fontWeight="700" fill="#c45a10">ω_WG (input)</text>

        {/* Flexspline CCW output arrow — bottom of gear */}
        <path d={`M ${cx - 20} ${cy + ryFS + 16} A ${rxFS + 16} ${ryFS + 16} 0 0 0 ${cx - rxFS - 10} ${cy + 22}`}
          fill="none" stroke="#7a2e00" strokeWidth="2" markerEnd="url(#arHD)" strokeDasharray="5,3"/>
        {/* ω_FS text below-left, outside gear */}
        <text x={50} y={cy + ryFS + 52} fontSize="9" fontWeight="700" fill="#7a2e00">ω_FS (output, slow)</text>

        {/* ── Callout labels — all strictly outside the gear boundary ── */}
        {/* Gear outer radius = Rcs+28 = 173. Left edge = cx-173 = 127. Top = cy-173 = 39. */}

        {/* Wave generator — top, above gear */}
        <line x1={cx} y1={cy - ryWG + 8} x2={cx} y2={cy - ryWG - 28} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={cx - 84} y={cy - ryWG - 58} width={168} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx} y={cy - ryWG - 40} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Wave Generator (WG)</text>
        <text x={cx} y={cy - ryWG - 29} textAnchor="middle" fontSize="8.5" fill="#a04010">elliptical cam + thin bearing · input</text>

        {/* Flexspline — bottom, below gear, left half */}
        {/* Leader from bottom-left of gear to label */}
        <line x1={cx - 60} y1={cy + Rcs + 24} x2={cx - 60} y2={cy + Rcs + 42} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={10} y={cy + Rcs + 40} width={170} height={32} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={95} y={cy + Rcs + 58} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Flexspline (FS)</text>
        <text x={95} y={cy + Rcs + 68} textAnchor="middle" fontSize="8.5" fill="#a04010">thin cup · ext. teeth · output</text>

        {/* Circular spline — bottom, below gear, right half */}
        <line x1={cx + 60} y1={cy + Rcs + 24} x2={cx + 60} y2={cy + Rcs + 42} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx + 8} y={cy + Rcs + 40} width={160} height={32} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx + 88} y={cy + Rcs + 58} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Circular Spline (CS)</text>
        <text x={cx + 88} y={cy + Rcs + 68} textAnchor="middle" fontSize="8.5" fill="#a04010">rigid ring · int. teeth · fixed</text>

        {/* ── Right panel: key facts ── */}
        <rect x={630} y={15} width={318} height={390} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={789} y={34} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Fundamental Relationships</text>
        <line x1={638} y1={40} x2={940} y2={40} stroke="#e8b07a"/>
        {[
          ['Tooth counts:', 'N_CS = N_FS + 2'],
          ['', '(CS always 2 more than FS)'],
          ['Gear ratio:', 'i = −N_FS / (N_CS − N_FS)'],
          ['', '= −N_FS / 2'],
          ['e.g. N_FS=100:', 'i = −100/2 = −50 : 1'],
          ['e.g. N_FS=200:', 'i = −200/2 = −100 : 1'],
          ['', ''],
          ['Output direction:', 'Opposite to WG rotation'],
          ['', '(negative sign in ratio)'],
          ['', ''],
          ['Typical ratios:', '30 : 1 to 320 : 1'],
          ['', 'from same-size package'],
          ['', ''],
          ['Teeth engaged:', '~10–30% of all teeth'],
          ['', 'simultaneously at mesh zones'],
          ['', '→ zero backlash'],
          ['', ''],
          ['Flexspline bends:', 'Twice per WG revolution'],
          ['', 'stresses < endurance limit'],
          ['', '→ infinite fatigue life'],
          ['', ''],
          ['Coaxial:', 'Input & output same axis'],
          ['Hollow shaft:', 'Cable routing possible'],
        ].map(([lbl, val], i) => (
          <g key={i}>
            <text x={640} y={55 + i*15} fontSize="9" fontWeight={lbl?'700':'400'} fill="#7a2e00">{lbl}</text>
            <text x={740} y={55 + i*15} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Three components: Wave Generator (input), Flexspline (output), Circular Spline (fixed) · tooth-count difference of 2 produces the enormous reduction ratio</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Operating Principle: 4-step rotation sequence
// ─────────────────────────────────────────────────────────────────────
function DiagramPrinciple() {
  const W = 900, H = 310;

  // Draw one step: WG at given angle, show flexspline deformed
  const Step = ({ cx, cy, wgAngle, label, note, nFS = 48, nCS = 50 }) => {
    const Rcs = 82, rxFS = 66, ryFS = 55, rxWG = 60, ryWG = 50;
    const cos = Math.cos(wgAngle), sin2 = Math.sin(wgAngle);

    // Highlight mesh points at major axis of WG ellipse
    const mxPos = cx + (rxFS + 4) * cos;
    const myPos = cy + (ryFS + 4) * sin2;
    const mxNeg = cx - (rxFS + 4) * cos;
    const myNeg = cy - (ryFS + 4) * sin2;

    return (
      <g>
        {/* CS ring */}
        <circle cx={cx} cy={cy} r={Rcs + 14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
        {/* CS internal teeth (simplified arc-segments) */}
        {Array.from({length: nCS}, (_, i) => {
          const a = (2*Math.PI*i)/nCS;
          const aW = (Math.PI * 0.45)/nCS;
          const pts = [
            [(Rcs)*Math.cos(a - aW*1.3), (Rcs)*Math.sin(a - aW*1.3)],
            [(Rcs-8)*Math.cos(a - aW*0.5), (Rcs-8)*Math.sin(a - aW*0.5)],
            [(Rcs-8)*Math.cos(a + aW*0.5), (Rcs-8)*Math.sin(a + aW*0.5)],
            [(Rcs)*Math.cos(a + aW*1.3), (Rcs)*Math.sin(a + aW*1.3)],
          ].map(([x,y]) => `${(cx+x).toFixed(1)},${(cy+y).toFixed(1)}`).join(' ');
          return <path key={i} d={`M ${pts} Z`} fill="#d4874a" stroke="#7a2e00" strokeWidth="0.8" opacity="0.8"/>;
        })}
        {/* Flexspline body */}
        <path d={flexsplinePath(cx, cy, rxFS, ryFS, nFS, 8, wgAngle)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.85"/>
        <path d={ellipsePath(cx, cy, rxFS - 12, ryFS - 10, wgAngle)}
          fill="#d4874a" stroke="none" opacity="0.6"/>
        {/* WG cam */}
        <path d={ellipsePath(cx, cy, rxWG, ryWG, wgAngle)}
          fill="#e8b07a" stroke="#7a2e00" strokeWidth="2" opacity="0.8"/>
        <path d={ellipsePath(cx, cy, rxWG - 12, ryWG - 10, wgAngle)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.9"/>
        <circle cx={cx} cy={cy} r={10} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        <circle cx={cx} cy={cy} r={4} fill="#7a2e00"/>

        {/* Mesh contact highlights */}
        <circle cx={mxPos} cy={myPos} r={10} fill="none" stroke="#7a2e00" strokeWidth="2" strokeDasharray="2,2" opacity="0.7"/>
        <circle cx={mxNeg} cy={myNeg} r={10} fill="none" stroke="#7a2e00" strokeWidth="2" strokeDasharray="2,2" opacity="0.7"/>

        {/* WG major-axis line */}
        <line x1={mxPos} y1={myPos} x2={mxNeg} y2={myNeg}
          stroke="#c45a10" strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>

        {/* Label */}
        <rect x={cx - 65} y={cy + Rcs + 16} width={130} height={34} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx} y={cy + Rcs + 30} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">{label}</text>
        <text x={cx} y={cy + Rcs + 43} textAnchor="middle" fontSize="8" fill="#a04010">{note}</text>
      </g>
    );
  };

  const steps = [
    { cx:100, label:'① WG at 0°',    note:'Mesh zones at 3 & 9 o\'clock', wgAngle: 0 },
    { cx:320, label:'② WG rotated 90° CW', note:'Mesh zones rotate to 12 & 6', wgAngle: Math.PI/2 },
    { cx:540, label:'③ WG rotated 180° CW',note:'FS moved back 1 tooth (÷2)',  wgAngle: Math.PI },
    { cx:760, label:'④ WG rotated 360° CW',note:'FS advanced 2 teeth total',    wgAngle: 0.12 },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Operating Principle — Wave Generator Rotation Drives Flexspline Backward</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arHD2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {steps.map((s, i) => (
          <Step key={i} cx={s.cx} cy={130} wgAngle={s.wgAngle} label={s.label} note={s.note}/>
        ))}

        {/* Step arrows */}
        {[198, 418, 638].map((x, i) => (
          <line key={i} x1={x} y1={130} x2={x+24} y2={130}
            stroke="#c45a10" strokeWidth="2" markerEnd="url(#arHD2)"/>
        ))}

        {/* Caption bar */}
        <rect x={10} y={282} width={880} height={24} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={450} y={295} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">
          Key: 180° WG rotation → FS moves back 1 tooth · 360° WG rotation → FS moves back 2 teeth
        </text>
        <text x={450} y={303} textAnchor="middle" fontSize="8" fill="#a04010">
          With N_FS=100 teeth: ratio = 100/2 = 50 · WG must rotate 50 times for 1 full FS revolution
        </text>
      </svg>
      <div style={S.dCaption}>The tooth-count difference (always 2) is the entire mechanism of reduction — the WG simply pushes the mesh zones around, ratcheting the FS backward by 2 teeth per revolution</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Gear Ratio Derivation & Calculation
// ─────────────────────────────────────────────────────────────────────
function DiagramRatio() {
  const W = 900, H = 290;

  const ratioData = [
    { nFS: 50,  nCS: 52,  label:'50T/52T', ratio: 25, barH: 25 },
    { nFS: 80,  nCS: 82,  label:'80T/82T', ratio: 40, barH: 40 },
    { nFS: 100, nCS: 102, label:'100T/102T', ratio: 50, barH: 50 },
    { nFS: 120, nCS: 122, label:'120T/122T', ratio: 60, barH: 60 },
    { nFS: 160, nCS: 162, label:'160T/162T', ratio: 80, barH: 80 },
    { nFS: 200, nCS: 202, label:'200T/202T', ratio: 100, barH: 100 },
    { nFS: 320, nCS: 322, label:'320T/322T', ratio: 160, barH: 130 },
  ];
  const barBase = 185, barW = 60, maxDisplayH = 130;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gear Ratio Derivation &amp; Ratio vs Tooth Count</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Derivation panel */}
        <rect x={10} y={10} width={340} height={265} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={180} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Ratio Derivation</text>
        <line x1={18} y1={34} x2={342} y2={34} stroke="#e8b07a"/>
        {[
          ['Constraint:', 'N_CS = N_FS + 2'],
          ['', ''],
          ['Per 360° of WG:', 'FS moves back 2 teeth'],
          ['', 'relative to CS'],
          ['', ''],
          ['Fractional FS turn:', 'φ_FS = 2 / N_FS  (in turns)'],
          ['', ''],
          ['Ratio (WG→FS):', 'i = −N_FS / 2'],
          ['', '(−ve = opposite direction)'],
          ['', ''],
          ['e.g. N_FS = 100:', 'i = −100/2 = −50'],
          ['', 'WG at 3000 rpm →'],
          ['', 'FS at −60 rpm'],
          ['', ''],
          ['Torque (ideal):', 'τ_FS = τ_WG × |i| × η'],
          ['', 'τ_FS = τ_WG × 50 × 0.85'],
          ['', ''],
          ['Alt: CS output,', 'i = N_CS / 2  (positive)'],
          ['FS fixed:', 'same direction as WG'],
        ].map(([lbl, val], i) => (
          <g key={i}>
            <text x={18} y={47 + i*12} fontSize="9" fontWeight={lbl?'700':'400'} fill={lbl?'#7a2e00':'#7a4a28'}>{lbl}</text>
            <text x={lbl?120:18} y={47 + i*12} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{val}</text>
          </g>
        ))}

        {/* Bar chart */}
        <line x1={375} y1={45} x2={375} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={375} y1={barBase+2} x2={890} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        {[0,40,80,120,160].map((v, i) => {
          const y = barBase - (v/160)*maxDisplayH;
          return (
            <g key={i}>
              <line x1={371} y1={y} x2={890} y2={y} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
              <text x={369} y={y+4} textAnchor="end" fontSize="8" fill="#7a4a28">{v}:1</text>
            </g>
          );
        })}
        <text x={370} y={38} textAnchor="end" fontSize="9" fill="#7a2e00" transform="rotate(-90,370,38)">Ratio</text>

        {ratioData.map((d, i) => {
          const x = 390 + i * 70;
          const h = (d.ratio / 160) * maxDisplayH;
          return (
            <g key={i}>
              <rect x={x} y={barBase - h} width={barW} height={h}
                fill="#c45a10" opacity={0.5 + i*0.06} rx={3}/>
              <text x={x + barW/2} y={barBase - h - 5} textAnchor="middle"
                fontSize="9" fontWeight="700" fill="#7a2e00">{d.ratio}:1</text>
              <text x={x + barW/2} y={barBase + 14} textAnchor="middle" fontSize="7.5" fill="#a04010">{d.label}</text>
            </g>
          );
        })}

        <text x={638} y={barBase + 28} textAnchor="middle" fontSize="8.5" fill="#7a4a28" fontStyle="italic">
          Tooth count (N_FS / N_CS) — ratio = N_FS / 2 regardless of absolute tooth size
        </text>
      </svg>
      <div style={S.dCaption}>Ratio scales linearly with tooth count · the same physical envelope gives 30:1 or 320:1 simply by changing the number of teeth — unique to harmonic drives</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Three Package Types: Cup, Hat, Pancake (Flat)
// ─────────────────────────────────────────────────────────────────────
function DiagramTypes() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Harmonic Drive Package Types — Cup, Hat &amp; Pancake (Flat)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* ── CUP TYPE ── */}
        <rect x={10} y={10} width={260} height={295} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={140} y={28} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Cup Type</text>
        <text x={140} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">Most common · compact axial length</text>

        {/* Cup flexspline side-section view */}
        {(() => {
          const cx = 140, cy = 155;
          return (
            <g>
              {/* CS outer ring */}
              <rect x={cx-80} y={cy-60} width={160} height={30} rx={4} fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.85"/>
              <text x={cx} y={cy-40} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">Circular Spline (fixed)</text>
              {/* FS cup shape (side view) */}
              <rect x={cx-62} y={cy-30} width={124} height={22} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.85"/>
              <rect x={cx-50} y={cy-8} width={14} height={55} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              <rect x={cx+36} y={cy-8} width={14} height={55} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              <rect x={cx-50} y={cy+47} width={100} height={12} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              <text x={cx} y={cy+8} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">Flexspline Cup</text>
              <text x={cx} y={cy+18} textAnchor="middle" fontSize="7" fill="#fdf4ec">(output)</text>
              {/* WG ellipse */}
              <ellipse cx={cx} cy={cy-18} rx={50} ry={14} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5" opacity="0.8"/>
              <text x={cx} y={cy-14} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">Wave Gen.</text>
              {/* Input shaft */}
              <rect x={cx-6} y={cy-75} width={12} height={16} rx={2} fill="#7a2e00"/>
              <text x={cx} y={cy-62} textAnchor="middle" fontSize="7.5" fill="#7a2e00">INPUT</text>
              {/* Output flange */}
              <rect x={cx-42} y={cy+60} width={84} height={10} rx={2} fill="#a04010" stroke="#7a2e00" strokeWidth="1.5"/>
              <text x={cx} y={cy+85} textAnchor="middle" fontSize="7.5" fill="#7a2e00">OUTPUT flange</text>
            </g>
          );
        })()}

        <text x={140} y={258} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• FS = shallow cup shape</text>
        <text x={140} y={270} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Short axial length</text>
        <text x={140} y={282} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Robot joints, servo axes</text>
        <text x={140} y={294} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• CSF, CSG series (Harmonic Drive)</text>

        {/* ── HAT TYPE ── */}
        <rect x={310} y={10} width={260} height={295} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={440} y={28} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Hat Type</text>
        <text x={440} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">Pancake profile · integrated output flange</text>

        {(() => {
          const cx = 440, cy = 155;
          return (
            <g>
              {/* CS ring */}
              <rect x={cx-80} y={cy-55} width={160} height={28} rx={4} fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.85"/>
              <text x={cx} y={cy-36} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">Circular Spline (fixed)</text>
              {/* Hat FS (brim extends out both sides) */}
              <rect x={cx-70} y={cy-26} width={140} height={18} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.85"/>
              {/* Hat brim sides */}
              <rect x={cx-70} y={cy-8} width={20} height={38} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              <rect x={cx+50} y={cy-8} width={20} height={38} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              {/* Hat crown (brim top) */}
              <rect x={cx-70} y={cy+30} width={140} height={12} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              <text x={cx} y={cy-12} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">Flexspline "Hat"</text>
              {/* WG */}
              <ellipse cx={cx} cy={cy-14} rx={48} ry={12} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5" opacity="0.8"/>
              <text x={cx} y={cy-10} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">WG</text>
              <rect x={cx-6} y={cy-70} width={12} height={16} rx={2} fill="#7a2e00"/>
              <text x={cx} y={cy-57} textAnchor="middle" fontSize="7.5" fill="#7a2e00">INPUT</text>
              {/* Output — bolt flange on brim */}
              {[-55,-30,30,55].map((dx,i)=>(
                <circle key={i} cx={cx+dx} cy={cy+42} r={4} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
              ))}
              <text x={cx} y={cy+82} textAnchor="middle" fontSize="7.5" fill="#7a2e00">OUTPUT (bolt flange)</text>
            </g>
          );
        })()}

        <text x={440} y={258} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• FS shaped like a top hat</text>
        <text x={440} y={270} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Brim = integrated output flange</text>
        <text x={440} y={282} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Very short axial length (pancake)</text>
        <text x={440} y={294} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• SHG, SHF series · hollow shaft</text>

        {/* ── FLAT (PANCAKE) TYPE ── */}
        <rect x={610} y={10} width={280} height={295} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={750} y={28} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Flat / Unit Type</text>
        <text x={750} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">Fully integrated gearhead unit</text>

        {(() => {
          const cx = 750, cy = 155;
          return (
            <g>
              {/* Outer casing */}
              <rect x={cx-90} y={cy-80} width={180} height={160} rx={8} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.6"/>
              {/* Motor flange */}
              <rect x={cx-55} y={cy-88} width={110} height={16} rx={4} fill="#a04010" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              <text x={cx} y={cy-76} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#fdf4ec">MOTOR FLANGE</text>
              {/* Internal gearing symbolic */}
              <ellipse cx={cx} cy={cy-20} rx={50} ry={16} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              <text x={cx} y={cy-16} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">WG + Bearing</text>
              <rect x={cx-56} y={cy} width={112} height={18} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              <text x={cx} y={cy+13} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#fdf4ec">Flexspline + CS</text>
              {/* Output bearing */}
              <rect x={cx-70} y={cy+22} width={140} height={16} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.6"/>
              <text x={cx} y={cy+33} textAnchor="middle" fontSize="7" fill="#fdf4ec">Cross-roller output bearing</text>
              {/* Output flange bottom */}
              <rect x={cx-55} y={cy+38} width={110} height={16} rx={4} fill="#a04010" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              <text x={cx} y={cy+50} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#fdf4ec">OUTPUT FLANGE</text>
              {/* Hollow shaft indicator */}
              <circle cx={cx} cy={cy-55} r={12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
              <circle cx={cx} cy={cy-55} r={5} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
              <text x={cx} y={cy-40} textAnchor="middle" fontSize="7.5" fill="#7a2e00">hollow shaft</text>
            </g>
          );
        })()}

        <text x={750} y={258} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Complete gearhead unit</text>
        <text x={750} y={270} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Cross-roller output bearing integral</text>
        <text x={750} y={282} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Bolt-on to any servo motor</text>
        <text x={750} y={294} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• CSG-GH, SHG-GH series</text>
      </svg>
      <div style={S.dCaption}>Cup and Hat types are component sets (three parts only) · Unit types add cross-roller bearing and housing for a complete drop-in gearhead solution</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Zero-Backlash Tooth Engagement
// ─────────────────────────────────────────────────────────────────────
function DiagramBacklash() {
  const W = 900, H = 290;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Zero-Backlash Mechanism — Multi-Tooth Engagement vs Conventional Gear</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* ── LEFT: Conventional gear pair — 1-2 teeth engaged, backlash gap visible ── */}
        <rect x={10} y={10} width={280} height={265} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={150} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Conventional Gear Pair</text>
        <text x={150} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">1–2 teeth in contact · backlash gap present</text>

        {/* Gear 1 */}
        <path d={gearPath(95, 140, 50, 16, 10)} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={95} cy={140} r={14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* Gear 2 */}
        <path d={gearPath(210, 140, 50, 16, 10, Math.PI/16)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={210} cy={140} r={14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>

        {/* Backlash gap annotation — kept left of both gears */}
        <rect x={140} y={128} width={30} height={24} rx={3} fill="#fff3cd" stroke="#c8a400" strokeWidth="1.5" opacity="0.9"/>
        <text x={155} y={143} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a5c00">GAP</text>
        <line x1={130} y1={152} x2={60} y2={172} stroke="#c8a400" strokeWidth="1.2"/>
        <text x={55} y={182} textAnchor="start" fontSize="7.5" fill="#7a5c00">backlash</text>

        {/* Contact tooth highlight */}
        <circle cx={152} cy={138} r={8} fill="none" stroke="#c45a10" strokeWidth="2" strokeDasharray="2,2"/>
        <text x={86} y={210} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Only 1–2 teeth at contact</text>
        <text x={86} y={223} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Angular play (backlash)</text>
        <text x={86} y={236} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Position error from gap</text>
        <text x={86} y={249} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Shock on reversal</text>

        {/* ── CENTRE: Harmonic drive — sweep of engaged teeth ── */}
        <rect x={310} y={10} width={280} height={265} rx={6} fill="none" stroke="#c45a10" strokeDasharray="5,3" strokeWidth="2"/>
        <text x={450} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Harmonic Drive</text>
        <text x={450} y={40} textAnchor="middle" fontSize="8.5" fill="#c45a10">10–30% of teeth engaged · ZERO backlash</text>

        {/* Show arc of engaged teeth */}
        {(() => {
          const cx = 450, cy = 140;
          const Rcs = 72, rxFS = 58, ryFS = 48;
          // CS ring portion
          return (
            <g>
              <circle cx={cx} cy={cy} r={Rcs+12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
              {/* CS teeth */}
              {Array.from({length:36},(_,i)=>{
                const a=(2*Math.PI*i)/36;
                const aW=(Math.PI*0.45)/36;
                const pts=[[Rcs*Math.cos(a-aW*1.3),Rcs*Math.sin(a-aW*1.3)],
                  [(Rcs-7)*Math.cos(a-aW*0.5),(Rcs-7)*Math.sin(a-aW*0.5)],
                  [(Rcs-7)*Math.cos(a+aW*0.5),(Rcs-7)*Math.sin(a+aW*0.5)],
                  [Rcs*Math.cos(a+aW*1.3),Rcs*Math.sin(a+aW*1.3)]
                ].map(([x,y])=>`${(cx+x).toFixed(1)},${(cy+y).toFixed(1)}`).join(' ');
                return <path key={i} d={`M ${pts} Z`} fill="#d4874a" stroke="#7a2e00" strokeWidth="0.8" opacity="0.7"/>;
              })}
              {/* FS ellipse teeth */}
              <path d={flexsplinePath(cx,cy,rxFS,ryFS,34,7)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.85"/>
              <path d={ellipsePath(cx,cy,rxFS-12,ryFS-10)} fill="#d4874a" stroke="none" opacity="0.5"/>
              {/* Highlight engaged zone — sweep arc left and right of major axis */}
              {[0, Math.PI].map((a,i)=>(
                <g key={i}>
                  <path d={`M ${cx+(rxFS+2)*Math.cos(a-0.45)} ${cy+(ryFS+2)*Math.sin(a-0.45)}
                    A ${Rcs-2} ${Rcs-2} 0 0 ${i===0?1:0} ${cx+(rxFS+2)*Math.cos(a+0.45)} ${cy+(ryFS+2)*Math.sin(a+0.45)}`}
                    fill="none" stroke="#7a2e00" strokeWidth="4" opacity="0.3"/>
                  <text x={cx+(rxFS+22)*Math.cos(a)} y={cy+(ryFS+22)*Math.sin(a)+4}
                    textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">engaged zone</text>
                </g>
              ))}
              {/* WG */}
              <path d={ellipsePath(cx,cy,rxFS-16,ryFS-14)} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5" opacity="0.8"/>
              <circle cx={cx} cy={cy} r={8} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
            </g>
          );
        })()}

        <text x={450} y={232} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• ~10–30% teeth engaged at once</text>
        <text x={450} y={244} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Both flanks in contact → zero play</text>
        <text x={450} y={256} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• No positional error from backlash</text>
        <text x={450} y={268} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Smooth torque reversal</text>

        {/* ── RIGHT: Comparison table ── */}
        <rect x={610} y={10} width={280} height={265} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={750} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Backlash Comparison</text>
        <line x1={618} y1={34} x2={882} y2={34} stroke="#e8b07a"/>
        {[
          ['', 'Harmonic', 'Planetary'],
          ['Backlash', '0 arc-sec', '3–10 arc-min'],
          ['Teeth engaged', '10–30%', '1 per planet'],
          ['Accuracy', '±30 arc-sec', '±1–5 arc-min'],
          ['Repeatability', '±5 arc-sec', '±30 arc-sec'],
          ['Torsional stiff.', 'Very high', 'High'],
          ['Ratio range', '30:1–320:1', '3:1–100:1'],
          ['Efficiency', '75–85%', '94–98%'],
          ['Weight', 'Very light', 'Light–Med'],
          ['Cost', 'High', 'Medium'],
          ['Life (bearing)', '7,000–20,000h', '>20,000h'],
        ].map(([lbl, h, p], i) => (
          <g key={i}>
            <rect x={618} y={37+i*20} width={262} height={20} fill={i%2===0?'#fdf4ec':'#fdf6ee'}/>
            <text x={622} y={51+i*20} fontSize={i===0?'9':'8.5'} fontWeight={i===0?'700':'600'} fill="#7a2e00">{lbl}</text>
            <text x={730} y={51+i*20} fontSize="8.5" fontFamily="'Courier New',monospace"
              fill={i===0?'#7a2e00':h==='0 arc-sec'||h==='10–30%'||h==='Very high'||h==='Very light'?'#c45a10':'#a04010'}>{h}</text>
            <text x={822} y={51+i*20} fontSize="8.5" fontFamily="'Courier New',monospace" fill={i===0?'#7a2e00':'#7a4a28'}>{p}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The elliptical deformation keeps teeth engaged on both flanks simultaneously — there is physically no room for backlash, unlike conventional gears which require a clearance gap</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Flexspline Stress & Fatigue Analysis
// ─────────────────────────────────────────────────────────────────────
function DiagramStress() {
  const W = 920, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Flexspline Stress Distribution &amp; Fatigue Cycle Analysis</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arST" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* ── Left: Cup cross-section with stress zones ── */}
        <rect x={10} y={10} width={310} height={280} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={165} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Flexspline Cup — Stress Zones</text>
        <line x1={18} y1={34} x2={312} y2={34} stroke="#e8b07a"/>

        {/* Cup side view */}
        {(() => {
          const cx = 165, baseY = 230;
          return (
            <g>
              {/* Cylinder walls */}
              <rect x={cx-55} y={90} width={14} height={120} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              <rect x={cx+41} y={90} width={14} height={120} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              {/* Bottom diaphragm */}
              <rect x={cx-55} y={210} width={110} height={16} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
              {/* Open end teeth */}
              {Array.from({length:12},(_,i)=>{
                const x = cx-52+i*9.5;
                return <rect key={i} x={x} y={82} width={8} height={10} rx={1} fill="#7a2e00" stroke="#fdf4ec" strokeWidth="0.8" opacity="0.85"/>;
              })}
              {/* Stress zone annotations */}
              {/* Zone 1: Tooth root (highest bending) */}
              <rect x={cx+60} y={78} width={108} height={26} rx={3} fill="#fdecea" stroke="#c0392b" strokeWidth="1.2"/>
              <text x={cx+114} y={91} textAnchor="middle" fontSize="8" fontWeight="700" fill="#c0392b">Zone 1: Tooth root</text>
              <text x={cx+114} y={101} textAnchor="middle" fontSize="7.5" fill="#c0392b">Highest bending stress</text>
              <line x1={cx+58} y1={91} x2={cx+44} y2={91} stroke="#c0392b" strokeWidth="1.2" markerEnd="url(#arST)"/>

              {/* Zone 2: Tooth flank (contact stress) */}
              <rect x={cx+60} y={110} width={108} height={26} rx={3} fill="#fdf0e4" stroke="#a04010" strokeWidth="1.2"/>
              <text x={cx+114} y={123} textAnchor="middle" fontSize="8" fontWeight="700" fill="#a04010">Zone 2: Tooth flank</text>
              <text x={cx+114} y={133} textAnchor="middle" fontSize="7.5" fill="#a04010">Contact stress (Hertz)</text>
              <line x1={cx+58} y1={123} x2={cx+44} y2={123} stroke="#a04010" strokeWidth="1.2" markerEnd="url(#arST)"/>

              {/* Zone 3: Wall bending */}
              <rect x={cx+60} y={150} width={108} height={26} rx={3} fill="#fdf4ec" stroke="#c45a10" strokeWidth="1.2"/>
              <text x={cx+114} y={163} textAnchor="middle" fontSize="8" fontWeight="700" fill="#c45a10">Zone 3: Wall bending</text>
              <text x={cx+114} y={173} textAnchor="middle" fontSize="7.5" fill="#c45a10">Cyclic hoop + bending</text>
              <line x1={cx+58} y1={163} x2={cx+44} y2={163} stroke="#c45a10" strokeWidth="1.2" markerEnd="url(#arST)"/>

              {/* Zone 4: Diaphragm */}
              <rect x={cx+60} y={200} width={108} height={26} rx={3} fill="#fdf6ee" stroke="#d4874a" strokeWidth="1.2"/>
              <text x={cx+114} y={213} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">Zone 4: Diaphragm</text>
              <text x={cx+114} y={223} textAnchor="middle" fontSize="7.5" fill="#7a4a28">Torsion + bending</text>
              <line x1={cx+58} y1={213} x2={cx+44} y2={213} stroke="#d4874a" strokeWidth="1.2" markerEnd="url(#arST)"/>

              {/* Cycle annotation */}
              <text x={cx} y={255} textAnchor="middle" fontSize="8.5" fill="#7a2e00" fontWeight="700">2 bending cycles per WG revolution</text>
              <text x={cx} y={267} textAnchor="middle" fontSize="8" fill="#7a4a28">Stresses well below endurance limit</text>
              <text x={cx} y={278} textAnchor="middle" fontSize="8" fill="#7a4a28">→ theoretically infinite fatigue life</text>
            </g>
          );
        })()}

        {/* ── Right: Stress cycle waveform ── */}
        <rect x={330} y={10} width={555} height={280} rx={6} fill="#fdf4ec" stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={607} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Bending Stress Cycle vs WG Rotation</text>
        <line x1={338} y1={34} x2={876} y2={34} stroke="#e8b07a"/>

        {/* Axes */}
        <line x1={370} y1={48} x2={370} y2={200} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={370} y1={200} x2={870} y2={200} stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={365} y={52} textAnchor="end" fontSize="8" fill="#7a4a28">σ</text>
        <text x={856} y={212} fontSize="8" fill="#7a4a28" textAnchor="end">θ (WG angle)</text>

        {/* Endurance limit lines */}
        <line x1={370} y1={82} x2={870} y2={82} stroke="#c45a10" strokeWidth="1" strokeDasharray="4,3" opacity="0.6"/>
        <line x1={370} y1={166} x2={870} y2={166} stroke="#c45a10" strokeWidth="1" strokeDasharray="4,3" opacity="0.6"/>
        <text x={373} y={78} fontSize="8" fill="#c45a10">+σ_endurance</text>
        <text x={373} y={180} fontSize="8" fill="#c45a10">−σ_endurance</text>

        {/* Actual stress wave — 2 full cycles per 360° WG */}
        {(() => {
          const x0=370, x1=870, y0=200, amplitude=55, yMid=124;
          const pts = Array.from({length:200},(_,i)=>{
            const t = i/199;
            const x = x0 + t*(x1-x0);
            const y = yMid - amplitude*Math.sin(4*Math.PI*t)*Math.exp(-0.3*t);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(' ');
          return <polyline points={pts} fill="none" stroke="#c45a10" strokeWidth="2.5"/>;
        })()}

        {/* Zero line */}
        <line x1={370} y1={124} x2={870} y2={124} stroke="#e8b07a" strokeWidth="1" strokeDasharray="3,3"/>
        <text x={367} y={128} textAnchor="end" fontSize="8" fill="#7a4a28">0</text>

        {/* Cycle labels */}
        {[0.125, 0.375, 0.625, 0.875].map((t, i) => {
          const x = 370 + t*500;
          return (
            <g key={i}>
              <line x1={x} y1={200} x2={x} y2={205} stroke="#7a4a28" strokeWidth="1"/>
              <text x={x} y={214} textAnchor="middle" fontSize="8" fill="#7a4a28">{i===0?'90°':i===1?'180°':i===2?'270°':'360°'}</text>
            </g>
          );
        })}

        {/* Shaded safe zone */}
        <rect x={370} y={82} width={500} height={84} fill="#c45a10" opacity="0.06"/>
        <text x={620} y={118} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10" opacity="0.7">Operating stress well inside endurance band</text>

        {/* Annotation panel */}
        <rect x={340} y={220} width={520} height={60} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={600} y={235} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Key design rule: σ_operating &lt; σ_endurance / S_f  (S_f = safety factor ≈ 1.5–2.0)</text>
        <text x={600} y={249} textAnchor="middle" fontSize="8.5" fill="#a04010">Flexspline bends 2× per WG rev → high cycle count at moderate stress → infinite life criterion</text>
        <text x={600} y={263} textAnchor="middle" fontSize="8.5" fill="#a04010">Material: high-strength alloy steel (40CrNiMo, 17-4PH stainless) · Heat treated to HRC 42–48</text>
        <text x={600} y={275} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Failure mode if overloaded: ratcheting (teeth skip), then plastic deformation of FS cup</text>
      </svg>
      <div style={S.dCaption}>Flexspline experiences 2 complete bending cycles per WG revolution — designed so operating stresses lie well below the endurance limit for infinite fatigue life</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Torsional Stiffness & Transmission Error
// ─────────────────────────────────────────────────────────────────────
function DiagramStiffness() {
  const W = 960, H = 270;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Torsional Stiffness Curve &amp; Transmission Error</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Torsional stiffness chart */}
        <rect x={10} y={10} width={430} height={250} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={225} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Torsional Stiffness vs Applied Torque</text>
        <line x1={18} y1={34} x2={432} y2={34} stroke="#e8b07a"/>

        {/* Axes */}
        <line x1={55} y1={48} x2={55} y2={195} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={55} y1={195} x2={425} y2={195} stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={50} y={52} textAnchor="end" fontSize="8" fill="#7a4a28">K_T</text>
        <text x={424} y={207} textAnchor="end" fontSize="8" fill="#7a4a28">Torque →</text>
        <text x={50} y={200} textAnchor="end" fontSize="8" fill="#7a4a28">0</text>

        {/* Stiffness curve — nonlinear, increases then flattens */}
        {(() => {
          const pts = Array.from({length:100},(_,i)=>{
            const t = i/99;
            const x = 55 + t*370;
            const y = 195 - (20 + 120*(1-Math.exp(-4*t)) + 15*t);
            return `${x.toFixed(1)},${Math.max(50,y).toFixed(1)}`;
          }).join(' ');
          return <polyline points={pts} fill="none" stroke="#c45a10" strokeWidth="2.5"/>;
        })()}

        {/* Region labels */}
        <rect x={60} y={160} width={80} height={22} rx={3} fill="#fdf4ec" stroke="#e8b07a"/>
        <text x={100} y={174} textAnchor="middle" fontSize="8" fill="#7a2e00">Low stiffness</text>
        <text x={100} y={183} textAnchor="middle" fontSize="7.5" fill="#7a4a28">(wind-up region)</text>

        <rect x={200} y={90} width={80} height={22} rx={3} fill="#fdf4ec" stroke="#c45a10"/>
        <text x={240} y={104} textAnchor="middle" fontSize="8" fill="#7a2e00">Stiffness rises</text>
        <text x={240} y={113} textAnchor="middle" fontSize="7.5" fill="#a04010">(teeth loading)</text>

        <rect x={330} y={65} width={80} height={22} rx={3} fill="#fdf0e4" stroke="#c45a10"/>
        <text x={370} y={79} textAnchor="middle" fontSize="8" fill="#7a2e00">Full stiffness</text>
        <text x={370} y={88} textAnchor="middle" fontSize="7.5" fill="#a04010">(rated zone)</text>

        {/* Y-axis labels */}
        {['K1','K2','K3'].map((l,i)=>(
          <g key={i}>
            <line x1={50} y1={175-i*52} x2={425} y2={175-i*52} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
            <text x={48} y={179-i*52} textAnchor="end" fontSize="8" fill="#7a4a28">{l}</text>
          </g>
        ))}

        <text x={225} y={220} textAnchor="middle" fontSize="8.5" fill="#7a4a28" fontStyle="italic">
          Non-linear K_T: low at near-zero torque (wind-up), increases to rated stiffness
        </text>
        <text x={225} y={232} textAnchor="middle" fontSize="8.5" fill="#7a4a28">
          Typical: K_T1 = 0.5–1 × K_rated (0–25% torque) · K_T3 = full rated stiffness
        </text>
        <text x={225} y={244} textAnchor="middle" fontSize="8.5" fill="#a04010">
          Wind-up (torsional spring effect) must be accounted for in precision control systems
        </text>

        {/* Transmission error chart */}
        <rect x={455} y={10} width={495} height={250} rx={6} fill="#fdf4ec" stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={702} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Transmission Error (TE) — Kinematic Error</text>
        <line x1={463} y1={34} x2={942} y2={34} stroke="#e8b07a"/>

        {/* Axes */}
        <line x1={490} y1={48} x2={490} y2={195} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={490} y1={195} x2={935} y2={195} stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={485} y={52} textAnchor="end" fontSize="8" fill="#7a4a28">TE</text>
        <text x={934} y={207} textAnchor="end" fontSize="8" fill="#7a4a28">WG angle →</text>

        {/* TE waveform — 2× per revolution harmonic dominates */}
        {(() => {
          const pts = Array.from({length:200},(_,i)=>{
            const t = i/199 * 4 * Math.PI;
            const x = 490 + (i/199)*440;
            const y = 122 - 32*Math.sin(2*t)*0.9 - 14*Math.sin(t) - 6*Math.cos(4*t);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(' ');
          return <polyline points={pts} fill="none" stroke="#c45a10" strokeWidth="2"/>;
        })()}

        <line x1={490} y1={122} x2={935} y2={122} stroke="#e8b07a" strokeWidth="1" strokeDasharray="3,3"/>
        <text x={487} y={126} textAnchor="end" fontSize="8" fill="#7a4a28">0</text>

        {/* Harmonics label */}
        <rect x={498} y={48} width={110} height={36} rx={3} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1"/>
        <text x={553} y={61} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">Dominant: 2× harmonic</text>
        <text x={553} y={72} textAnchor="middle" fontSize="7.5" fill="#a04010">(2 mesh zones per rev)</text>
        <text x={553} y={82} textAnchor="middle" fontSize="7.5" fill="#7a4a28">± 30–120 arc-sec typical</text>

        <text x={702} y={220} textAnchor="middle" fontSize="8.5" fill="#7a4a28" fontStyle="italic">
          TE = angular deviation of output from ideal position under no-load
        </text>
        <text x={702} y={232} textAnchor="middle" fontSize="8.5" fill="#7a4a28">
          Caused by: tooth geometry errors, FS wall thickness variation, ellipse form error
        </text>
        <text x={702} y={244} textAnchor="middle" fontSize="8.5" fill="#a04010">
          Reducible by: precision grinding of teeth (S-tooth profile), tight assembly tolerances
        </text>
      </svg>
      <div style={S.dCaption}>Torsional stiffness is non-linear — the "wind-up" at low torque is a known limitation of harmonic drives in precision servo systems · transmission error has a dominant 2× frequency</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — Efficiency & Loss Breakdown
// ─────────────────────────────────────────────────────────────────────
function DiagramEfficiency() {
  const W = 960, H = 260;

  const losses = [
    { label: 'Bearing\nLoss', pct: 7.0, x: 55,  desc: 'WG elliptical bearing — dominant loss source' },
    { label: 'Tooth\nMesh', pct: 4.5, x: 185, desc: 'Sliding at engagement/disengagement zones' },
    { label: 'FS Flex\nHysteresis', pct: 2.5, x: 315, desc: 'Elastic deformation energy dissipated as heat' },
    { label: 'Churning\n(Grease)', pct: 1.5, x: 445, desc: 'Lubrication drag — reduced by correct fill' },
    { label: 'Seal\nFriction', pct: 0.5, x: 575, desc: 'Shaft seals and housing drag' },
  ];
  const barBase = 175, maxH = 110, maxPct = 10, barW = 80;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Efficiency &amp; Power Loss Sources — Typical Values at Rated Torque</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Banner — only spans bar chart area, stops well before total loss panel */}
        <rect x={10} y={8} width={685} height={28} rx={4} fill="#fdf0e4" stroke="#c45a10"/>
        <text x={352} y={20} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">
          Overall η ≈ 75–85% at rated torque · lower than planetary (~97%) · improves at high speed &amp; low ratio
        </text>
        <text x={352} y={31} textAnchor="middle" fontSize="8.5" fill="#a04010">
          η = P_out / P_in = τ_out·ω_out / (τ_in·ω_in)  ·  P_loss = heat in WG bearing + flexspline
        </text>

        {/* Axes */}
        <line x1={32} y1={44} x2={32} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={32} y1={barBase+2} x2={700} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        {[0,2,4,6,8,10].map((v,i)=>{
          const y = barBase-(v/maxPct)*maxH;
          return (
            <g key={i}>
              <line x1={28} y1={y} x2={700} y2={y} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
              <text x={26} y={y+4} textAnchor="end" fontSize="8" fill="#7a4a28">{v}%</text>
            </g>
          );
        })}
        <text x={14} y={110} textAnchor="middle" fontSize="9" fill="#7a2e00" transform="rotate(-90,14,110)">Loss %</text>

        {losses.map((l, i) => {
          const h = (l.pct/maxPct)*maxH;
          const lines = l.label.split('\n');
          return (
            <g key={i}>
              <rect x={l.x} y={barBase-h} width={barW} height={h}
                fill="#c45a10" opacity={0.55+i*0.08} rx={3}/>
              <text x={l.x+barW/2} y={barBase-h-6} textAnchor="middle"
                fontSize="10" fontWeight="700" fill="#7a2e00">{l.pct}%</text>
              {lines.map((line, li) => (
                <text key={li} x={l.x+barW/2} y={barBase+14+li*13}
                  textAnchor="middle" fontSize="8.5" fill="#7a4a28">{line}</text>
              ))}
            </g>
          );
        })}

        {/* Total loss panel — starts at x=710, safely separated from bars */}
        <rect x={710} y={8} width={240} height={220} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={830} y={26} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Total Loss Budget</text>
        <line x1={718} y1={32} x2={942} y2={32} stroke="#e8b07a"/>
        {[
          ['Bearing:', '~7%'],
          ['Tooth mesh:', '~4.5%'],
          ['FS hysteresis:', '~2.5%'],
          ['Churning:', '~1.5%'],
          ['Seals:', '~0.5%'],
          ['TOTAL LOSS:', '~16%'],
          ['Efficiency:', '~84%'],
          ['', ''],
          ['Speed effect:', 'η ↑ at high speed'],
          ['Torque effect:', 'η ↑ at high torque'],
          ['Temp effect:', 'η ↑ when warm'],
          ['Ratio effect:', 'η ↑ lower ratio'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={718} y={44+i*13} fontSize="9" fontWeight={l.includes('TOTAL')||l.includes('Efficiency')?'700':'400'}
              fill={l.includes('TOTAL')||l.includes('Efficiency')?'#c45a10':'#7a2e00'}>{l}</text>
            <text x={810} y={44+i*13} fontSize="9" fontFamily="'Courier New',monospace"
              fill={l.includes('TOTAL')||l.includes('Efficiency')?'#c45a10':'#a04010'}>{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>WG bearing loss dominates — the elliptical bearing experiences high alternating loads · efficiency is lower than planetary gears but the precision and zero-backlash advantage is decisive in robotics</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 9 — Applications
// ─────────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 360;
  const apps = [
    { title:'Industrial\nRobot Joint', icon:'🦾', lines:['6-axis robot axes 4-6','Zero backlash critical','50:1–160:1 per joint','Hollow shaft for cables'] },
    { title:'Space &\nAerospace', icon:'🛸', lines:['NASA Apollo LRV 1971','Hubble solar panels','Mars rover joints','Vacuum, cryogenic rated'] },
    { title:'Semiconductor\nEquipment', icon:'💿', lines:['Wafer handler arms','Sub-micron positioning','Clean room compatible','High repeatability'] },
    { title:'Medical\nDevices', icon:'🔬', lines:['Surgical robot joints','MRI-compatible variants','Sterilisable designs','Precise force control'] },
    { title:'Machine Tool\nAxes', icon:'⚙️', lines:['Rotary table drives','Precision indexing','High torque stiffness','Sub-arcsecond accuracy'] },
    { title:'CNC &\nSurveying', icon:'📡', lines:['Telescope mounts','Radar antenna drives','Camera stabilisers','Smooth slow tracking'] },
  ];
  const cardH = 148, rowGap = 22;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Harmonic Drive Applications Across Engineering Disciplines</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        {apps.map((a, i) => {
          const col = i%3, row = Math.floor(i/3);
          const x = 80 + col*285, y = 18 + row*(cardH+rowGap);
          const lines = a.title.split('\n');
          return (
            <g key={i}>
              <rect x={x-65} y={y} width={146} height={cardH} rx={6}
                fill={i%2===0?'#fdf4ec':'#fdf0e4'} stroke="#e8b07a" strokeWidth="1.5"/>
              <circle cx={x} cy={y+32} r={26} fill="#c45a10" opacity="0.85" stroke="#7a2e00" strokeWidth="2"/>
              <text x={x} y={y+39} textAnchor="middle" fontSize="18">{a.icon}</text>
              {lines.map((l,li)=>(
                <text key={li} x={x} y={y+68+li*14} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{l}</text>
              ))}
              <line x1={x-55} y1={y+96} x2={x+55} y2={y+96} stroke="#e8c89a"/>
              {a.lines.map((l,li)=>(
                <text key={li} x={x} y={y+108+li*12} textAnchor="middle" fontSize="8" fill="#a04010">{l}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Harmonic drives are the standard solution wherever zero backlash, high reduction ratio, and extreme precision are simultaneously required in a compact envelope</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────
export default function HarmonicDrives() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Harmonic Drive Systems in Mechanical Engineering</h1>
      <p style={S.p}>
        A <strong>harmonic drive</strong> (also called a <em>strain wave gear</em>) is a precision gear system that
        exploits the elastic deformation of metal to achieve extremely high reduction ratios — up to 320:1 — from only
        three components, in the same envelope that a planetary gearbox might deliver 10:1. Invented by C. Walton Musser
        in 1955 and first commercialised by Harmonic Drive SE, the technology is now the dominant choice for robot joints,
        aerospace mechanisms, and precision instrumentation worldwide, offering <strong>zero backlash</strong>,
        exceptional positional repeatability, and coaxial input/output in a compact, lightweight package.
      </p>

      <InfoBox title="Quick Reference — Core Facts &amp; Formulae" items={[
        "Three components only: Wave Generator (input), Flexspline (output), Circular Spline (fixed)",
        "Tooth count rule:  N_CS = N_FS + 2  — circular spline always has exactly 2 more teeth than flexspline",
        "Gear ratio:  i = −N_FS / 2  (negative = output reverses direction) · e.g. 100-tooth FS → 50:1",
        "Typical ratios: 30:1 to 320:1 in a single stage · from the same physical package",
        "Zero backlash: 10–30% of teeth engaged simultaneously · both flanks in contact at all times",
        "Efficiency: η ≈ 75–85% at rated torque (lower than planetary — WG bearing dominates losses)",
        "Flexspline bends 2× per WG revolution — designed for infinite fatigue life at rated load",
        "Positional accuracy: ±30 arc-seconds · Repeatability: ±5 arc-seconds (class dependent)",
        "Torsional stiffness is non-linear — 'wind-up' effect at low torque must be modelled in control systems",
        "Lubrication: specific greases (Mobilux EP1, Harmonic Grease 4A) or synthetic oil for high-speed",
      ]}/>

      <h2 style={S.h2}>Complete Anatomy — Components &amp; Geometry</h2>
      <p style={S.p}>
        Every harmonic drive contains exactly three functional parts. The <em>wave generator</em> (WG) is an elliptical
        cam fitted with a thin-race ball bearing — it is the high-speed input. The <em>flexspline</em> (FS) is a thin-walled
        steel cup with external teeth that deforms to match the WG ellipse — it is the output. The <em>circular spline</em>
        (CS) is a rigid ring with internal teeth, fixed to the housing. All three are coaxial.
      </p>
      <DiagramAnatomy />
      <Table2 rows={[
        ["Wave Generator (WG)","Elliptical cam hub + thin-race ball bearing (flexible outer race)\nPresses inside the FS, deforming it into an elliptical shape\nAlways the input — connected to motor shaft · highest speed member"],
        ["Flexspline (FS)","Thin-walled alloy steel cup · wall thickness ~0.2–2 mm depending on size\nExternal teeth on the open (deformable) end · rigid base = output\nRadially compliant but torsionally very stiff · N_FS = N_CS − 2"],
        ["Circular Spline (CS)","Rigid steel ring with internal teeth · always has 2 more teeth than FS\nTypically fixed to housing as the reaction member\nCan be made the output (with FS fixed) — gives positive ratio + same direction"],
        ["Coaxial geometry","All three members share the same rotational axis — unique vs. planetary\nInput shaft and output shaft are coaxial → clean integration\nHollow through-shaft option available for cable/laser routing"],
        ["Tooth count geometry","N_CS = N_FS + 2 is the only constraint on tooth counts\nRatio depends only on N_FS, not on tooth size or module\nSame OD package → different ratio simply by changing N_FS (and module)"],
        ["Ellipse geometry","WG semi-axes: a = r_nominal + δ, b = r_nominal − δ  (δ = radial deflection)\nDeflection δ ≈ r_nominal / i (small fraction of radius)\nDeflection is distributed uniformly around circumference by ball bearing"],
      ]}/>

      <h2 style={S.h2}>Operating Principle — How the Reduction Happens</h2>
      <p style={S.p}>
        The mechanism is surprisingly simple: the WG rotates, sweeping its elliptical major axis around. At any instant,
        the FS teeth only mesh with the CS teeth at two points on opposite ends of the major axis. Because the FS has
        2 fewer teeth than the CS, every 180° of WG rotation advances the FS by exactly 1 tooth backward relative to the CS.
        After 1 full WG revolution (360°), the FS has moved backward by 2 teeth — giving a ratio of N_FS/2.
      </p>
      <DiagramPrinciple />
      <Table2 rows={[
        ["Mesh zone mechanics","Teeth engage only at 2 diametrically-opposite regions along WG major axis\nAs WG rotates, mesh zones sweep around the ring continuously\nAt any instant: ~10–30% of all teeth are in mesh (vs 1–2 for conventional gears)"],
        ["Tooth advancement","Per 180° WG rotation: FS advances 1 tooth backward relative to CS\nPer 360° WG rotation: FS advances 2 teeth backward → i = N_FS/2\nFor N_FS = 160: i = 80, so WG needs 80 revolutions for 1 FS revolution"],
        ["Direction of rotation","FS rotates opposite to WG direction (negative ratio) when CS is fixed\nAlternate mode: fix FS, use CS as output → same direction as WG, i = N_CS/2\nDouble-stage: two sets in series for ratio up to 500:1+"],
        ["No slip condition","Unlike belt drives: no slip is possible — every tooth engagement is positive\nVelocity ratio is exact and constant at every instant\nNo speed variation due to pitch circle approximation (unlike cycloidal drives)"],
        ["Why 2 teeth?","The difference must be an even number to maintain symmetry (2 mesh points)\nDifference of 2 gives maximum practical ratio for given tooth count\nDifference of 4, 6 gives lower ratios (e.g. i = N_FS/4) — less common"],
      ]}/>

      <h2 style={S.h2}>Gear Ratio Derivation &amp; Calculation</h2>
      <DiagramRatio />
      <Table2 rows={[
        ["Standard ratio formula","i = −N_FS / (N_CS − N_FS) = −N_FS / 2\n(negative sign: output opposes WG direction when CS is fixed)\nNumerically: ratio magnitude = N_FS / 2"],
        ["Alternate mode (CS output)","Fix FS, use CS as output: i = +N_CS / 2  (same direction as WG)\nLess common — CS output requires rotating the heavy ring gear\nUsed in some differential and split-power configurations"],
        ["Typical tooth counts","Standard: N_FS = 100–320, N_CS = 102–322\nSmall precision units: N_FS = 50–80 (ratio 25:1 to 40:1)\nLarge industrial: N_FS = 200–320 (ratio 100:1 to 160:1)"],
        ["Output speed","ω_out = ω_in / i  (magnitude)  ·  direction reversed from input\nExample: motor at 3000 rpm, i=100:1 → output at 30 rpm"],
        ["Output torque","τ_out = τ_in × i × η  (η ≈ 0.75–0.85)\nExample: τ_in = 0.5 N·m → τ_out = 0.5 × 100 × 0.80 = 40 N·m"],
        ["Size independence","Ratio is independent of gear physical size — only tooth count matters\nSame OD gearbox: 30:1 or 160:1 — no size, weight penalty for higher ratio\nThis is unique: in planetary gearboxes, higher ratio requires larger package"],
      ]}/>

      <h2 style={S.h2}>Package Types — Cup, Hat &amp; Unit</h2>
      <p style={S.p}>
        The three-component harmonic drive mechanism can be packaged in different physical configurations depending
        on the application. Cup and Hat types supply only the three bare gearing components; Unit types add
        a cross-roller output bearing and housing for a complete, motor-mountable gearhead.
      </p>
      <DiagramTypes />
      <Table2 rows={[
        ["Cup type (CSF/CSG)","Flexspline shaped as a shallow cup (open end with teeth, closed base = output)\nMost widely used · shortest overall axial length of the three gear types\nUsed in: robot wrist joints, servo axis gearheads, aerospace mechanisms"],
        ["Hat type (SHG/SHF)","Flexspline shaped like a top hat — brim extends radially outward\nBrim serves as integrated bolt-flange output — no separate output coupling needed\nExtremely short axial profile ('pancake') · ideal for hollow-shaft through-shaft designs"],
        ["Flat/Unit type (CSG-GH)","Complete gearhead: WG + FS + CS + cross-roller output bearing + housing\nBolt directly onto IEC/NEMA servo motors · drop-in replacement for standard gearboxes\nCross-roller bearing provides high moment stiffness and accuracy as part of unit"],
        ["Component set vs unit","Component set: designer integrates into their own housing and bearing arrangement\nUnit: fully enclosed, pre-loaded, pre-lubricated, ready to mount\nComponent sets: lighter, more compact · Units: easier integration, defined output accuracy"],
        ["Hollow shaft option","Most types available with large through-hole (inner diameter up to ~50% of OD)\nAllows routing of cables, pneumatic lines, optical fibers, or secondary shafts\nCritical for robot joint design — eliminates external cable management"],
      ]}/>

      <h2 style={S.h2}>Zero Backlash &amp; Precision Comparison</h2>
      <DiagramBacklash />
      <Table2 rows={[
        ["Why zero backlash","Both flanks of FS and CS teeth are in contact simultaneously across mesh zone\nNo tooth clearance gap is possible — ellipse maintains constant preload\nOnce set at assembly, backlash remains zero for life (worn teeth reduce stiffness but not backlash)"],
        ["Positional accuracy","Accuracy: ±30 arc-sec (standard) to ±10 arc-sec (precision grade)\nRepeatability: ±5 arc-sec standard · ±1 arc-sec high-precision grade\nLimited by TE (transmission error), not by backlash"],
        ["S-tooth profile","Harmonic Drive's proprietary curved tooth profile (S-tooth)\nOptimised for simultaneous multi-tooth engagement and stress distribution\nUp to 30% more teeth engaged vs involute profile → lower per-tooth stress"],
        ["Transmission error (TE)","TE = angular deviation of output from theoretical ideal under no load\nDominant frequency: 2× WG rotation (two mesh zones per revolution)\nTypical amplitude: ±30–120 arc-seconds depending on size and accuracy class"],
        ["Accuracy vs planetary","Backlash: Harmonic 0 arc-sec vs Planetary 3–10 arc-min\nRepeatability: Harmonic ±5 arc-sec vs Planetary ±30 arc-sec\nTE: Harmonic ~2× per rev vs Planetary mesh frequency × N_planets"],
      ]}/>

      <h2 style={S.h2}>Flexspline Stress &amp; Fatigue Life</h2>
      <p style={S.p}>
        The flexspline is the most stressed component in a harmonic drive. It undergoes cyclic bending every half
        revolution of the wave generator. However, the design criterion ensures that operating stresses remain well
        below the material endurance limit, giving theoretically infinite fatigue life when operated within ratings.
      </p>
      <DiagramStress />
      <Table2 rows={[
        ["Bending cycle frequency","FS bends 2× per WG revolution (two ellipse poles sweep past)\nAt 3000 rpm WG: 6000 bending cycles/minute = 100 Hz\nOver 20,000 h life: ~7.2 × 10⁹ cycles — infinite life criterion applies"],
        ["Critical stress zones","Tooth root bending: highest alternating stress — failure if overloaded\nDiaphragm torsion: transmits output torque from FS cup to output shaft\nWall bending: hoop + bending combination · governed by ellipse eccentricity"],
        ["Material specification","High-alloy steel: 40CrNiMo, 17-4PH stainless, Maraging 350\nHeat treatment: HRC 42–48 (core) · surface: carburised or nitrided\nTensile strength: 1000–1500 MPa · Endurance limit: 450–700 MPa"],
        ["Failure modes","Ratcheting: FS teeth skip over CS at extreme overload (16–17× rated torque)\nPlastic deformation: FS cup buckles at very high sustained overload\nFatigue crack: initiates at tooth root under long-term overload cycling\nWear: tooth flank wear from insufficient lubrication → backlash develops"],
        ["Safety factors","Operating stress / endurance limit: typically 0.4–0.7 (factor of 1.4–2.5)\nCatalog ratings already incorporate appropriate safety factors\nPeak torque (instantaneous): up to 3× rated · Momentary: up to 5× rated"],
        ["Temperature effects","Operating range: −30°C to +90°C (standard) · −55°C to +125°C (space grade)\nHigh temperature: reduces material strength and lubricant viscosity\nLow temperature: increases grease stiffness, potential for increased TE"],
      ]}/>

      <h2 style={S.h2}>Torsional Stiffness &amp; Transmission Error</h2>
      <DiagramStiffness />
      <Table2 rows={[
        ["Torsional stiffness","Non-linear: low stiffness at near-zero torque ('wind-up'), rises to rated K_T\nThree-region model: K_T1 (0–25% rated), K_T2 (25–75% rated), K_T3 (75–100% rated)\nTypical rated K_T: 2–300 kN·m/rad depending on frame size"],
        ["Wind-up effect","At low torque, FS twists slightly before transmitting force (spring effect)\nPosition error = τ / K_T1 (must be compensated in servo control)\nWind-up is reversible — lost position recovered on load reversal"],
        ["Transmission error","TE measured under no-load as angular deviation vs ideal position\nDominant component: 2nd harmonic of WG rotation (frequency = 2 × ω_WG)\nCauses: tooth profile error, FS wall thickness variation, eccentricity"],
        ["Reducing TE","Precision grinding of teeth (S-tooth profile)\nTight assembly tolerances for WG bearing eccentricity (< 0.01 mm)\nPre-loading the assembly · Temperature stabilisation before precision use"],
        ["Control implications","Wind-up introduces a compliant element in the servo loop → bandwidth limitation\nModel as torsional spring K_T in the servo transmission model\nObserver-based compensation possible but requires accurate K_T model"],
        ["Stiffness vs ratio","Torsional stiffness is independent of reduction ratio for same frame size\nOnly the output torque capacity and stress scale with ratio\nThis allows the same gearhead housing to offer multiple ratios"],
      ]}/>

      <h2 style={S.h2}>Efficiency &amp; Power Loss</h2>
      <DiagramEfficiency />
      <Table2 rows={[
        ["Overall efficiency","η ≈ 75–85% at rated torque and rated speed (typical operating point)\nLower than planetary (94–98%) — this is the primary engineering trade-off\nImproves at: higher speed, lower ratio, full torque, warm grease temperature"],
        ["Dominant loss: WG bearing","Elliptical bearing experiences large alternating radial loads → high friction\nThin-race bearing has higher rolling resistance than standard bearings\nImproveable by: angular contact bearing, better cage design, lower-viscosity lubricant"],
        ["Lubrication critical","Correct lubricant: Mobilux EP1 grease, Harmonic Grease 4A (PFPE for vacuum)\nOver-filling causes churning loss → efficiency drop + temperature rise\nUnder-filling causes premature bearing wear → shortened life\nRecommended fill: ~30% of internal cavity volume"],
        ["Back-driveability","Harmonic drives are back-driveable (output can drive input) unlike worm gears\nBack-drive efficiency: η_back = 2 - 1/η_forward ≈ 0.5–0.7\nRobot joints must absorb back-driving torque via motor brake or servo hold"],
        ["Thermal management","At continuous rated load: temperature rise 30–60°C above ambient\nMaximum continuous temperature: 90°C at output bearing\nForced cooling or thermal derating required for continuous high-duty applications"],
        ["Efficiency vs planetary","Harmonic: 75–85% · Planetary: 94–98%\nDifference: ~12–15% more losses per stage in harmonic drive\nJustification: zero backlash, 10× smaller, precision unachievable by planetary"],
      ]}/>

      <h2 style={S.h2}>Design &amp; Selection Procedure</h2>
      <Table2 rows={[
        ["Step 1 — Define loads","Peak torque, rated torque, average torque, shock factor, duty cycle\nApply service factor K_s ≥ 1.0–2.5 depending on impact, vibration, duty\nτ_required = τ_application × K_s — must not exceed catalog peak torque"],
        ["Step 2 — Select ratio","Required ratio i = ω_motor / ω_output\nChoose nearest standard ratio (most common: 30, 50, 80, 100, 120, 160, 200, 320)\nVerify output speed = ω_motor / i meets application speed requirement"],
        ["Step 3 — Select frame size","Based on rated torque at required speed from catalog\nCheck: average torque < rated torque · peak torque < peak allowable\nVerify: life at average torque ≥ required hours (L10 bearing life calculation)"],
        ["Step 4 — Check bearing life","Life limited by WG bearing: L10 = (C/P)³ × 10⁶/60n  (hours)\nC = dynamic load capacity · P = equivalent radial load from torque\nTypical: 7,000–20,000 h depending on frame, ratio, operating conditions"],
        ["Step 5 — Verify stiffness","Check torsional stiffness K_T meets position accuracy requirement\nPositional error = τ_max / K_T1 must be within allowable\nAdd wind-up to TE for total expected positional deviation"],
        ["Step 6 — Thermal check","Power loss = P_in × (1−η) → heat generated per second\nEstimate temperature rise: ΔT = P_loss / (h × A_housing)\nIf ΔT > 50°C: add cooling, derate, or select larger frame size"],
        ["Step 7 — Lubrication","Select lubricant per catalog (Harmonic Grease 4A standard, PFPE for vacuum)\nFill quantity: typically 10–40% of internal cavity volume (exact per catalog)\nChange interval: typically 5,000–10,000 h or annually (whichever first)"],
        ["Step 8 — Integration","WG must be fixed axially AND torsionally to input shaft (both directions)\nFS must be isolated from external radial/axial loads — use cross-roller bearing\nAlignment: concentricity of three components critical — follow assembly procedure"],
      ]}/>

      <h2 style={S.h2}>Applications</h2>
      <DiagramApplications />
      <Table2 rows={[
        ["Industrial robotics","Axes 4, 5, 6 (wrist joints) of 6-axis arms — compact, zero-backlash critical\n100:1–160:1 per joint · hollow shaft routes power cables through joint\nMajority of global precision robot joints use harmonic drives"],
        ["Space & aerospace","Apollo 15 Lunar Roving Vehicle (1971): 80:1 WD motor drives\nHubble Space Telescope solar panel deployment and fine pointing\nMars Exploration Rover arm joints, camera pan-tilt platforms\nSpace grade: vacuum-compatible PFPE lubricant, radiation-hardened bearings"],
        ["Semiconductor equipment","Wafer handling robots in clean-room: zero backlash = no particle generation\nSub-micron positioning accuracy · ultra-clean, outgassing-free variants\nLithography stages, plasma etch handlers, inspection systems"],
        ["Medical robotics","da Vinci surgical robot wrist joints: extreme dexterity in confined spaces\nMRI-compatible stainless steel variants for imaging-guided surgery\nRehabilitation exoskeletons · prosthetic joint actuators"],
        ["Precision instruments","Telescope and antenna pointing drives: sub-arcsecond tracking\nRing laser gyroscope gimbals · coordinate measuring machine axes\nOptical bench positioning, adaptive optics mirror drives"],
        ["Machine tools & CNC","Rotary table (4th axis) · indexing heads · tool changer magazines\nHigh-torque precision positioning without backlash-induced error\nGrinding machine dressing spindles · gear hobbing dividing heads"],
      ]}/>

      <h2 style={S.h2}>Failure Modes &amp; Maintenance</h2>
      <Table2 rows={[
        ["WG bearing failure","Most common end-of-life mode — fatigue spalling of thin-race bearing\nSymptoms: increased vibration/noise, rising temperature, stiffness loss\nPrevention: correct lubricant fill, avoid misalignment, do not exceed rated loads"],
        ["Flexspline fatigue crack","Occurs only under severe sustained overload or repeated shock above peak rating\nInitiates at tooth root or diaphragm/wall junction\nCan be detected by increased backlash, output irregularity, metallic particles in grease"],
        ["Ratcheting","FS teeth skip over CS teeth when torque exceeds ratcheting torque (catalogued)\nTypically occurs at 8–12× rated torque · no permanent damage if brief\nPrevention: torque limiting coupling on input, correct service factor selection"],
        ["Lubrication failure","Insufficient or degraded grease → rapid bearing wear → increased TE and temperature\nContamination with water or incompatible lubricant causes rapid degradation\nFix: drain, clean, relubricate with specified grease at specified fill volume"],
        ["Dedoidal engagement","WG installed incorrectly (not last) → eccentric tooth mesh → noise, vibration, early failure\nStrict assembly sequence: CS → FS → WG (wave generator inserted last always)\nSymptom: abnormal noise at 2× WG frequency from first operation"],
        ["Inspection intervals","Check vibration signature at WG bearing frequency: f_bearing = n × Z × rpm/60\nMonitor temperature rise — increase >20°C above baseline indicates lubrication issue\nRelubricate: every 3,000–5,000 h · Replace WG bearing: per L10 life calculation"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Tooth count:  N_CS = N_FS + 2  (always — any difference must be even)",
        "Gear ratio:  i = −N_FS / 2  (CS fixed, FS output, WG input) · i = +N_CS / 2  (FS fixed, CS output)",
        "Output speed:  ω_FS = −ω_WG / (N_FS / 2)  = −2 · ω_WG / N_FS",
        "Output torque:  τ_FS = τ_WG × (N_FS/2) × η  where η ≈ 0.75–0.85",
        "Efficiency:  η = P_out / P_in = (τ_out × ω_out) / (τ_in × ω_in)",
        "Bending cycles:  N_cycles = 2 × n_WG (revolutions)  — 2 per WG revolution",
        "Torsional wind-up:  θ_error = τ_applied / K_T1  (rad) — at low torque region",
        "Bearing L10 life:  L10 = (C/P)³ × 10⁶ / (60 × n_WG)  (hours)",
        "Position accuracy:  δθ = TE_amplitude + τ/K_T  (total expected deviation)",
        "Radial deflection:  δ = r_nominal × (a/b − 1)  where a,b = WG semi-axes",
      ]}/>
    </div>
  );
}